import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { IconButton, Icons, cx } from '../../components/ui.tsx';
import { SelectBox } from '../drive/SelectionBar.tsx';
import { BRAND, BrandIcon } from '../../components/icons.tsx';
import { ContextMenu, useContextMenu, type MenuItem } from '../../components/ContextMenu.tsx';
import { ShareLinkDialog } from '../drive/ShareLinkDialog.tsx';
import { StarButton, useStarMenuItem } from '../drive/StarButton.tsx';
import { PropertiesPanel } from '../../components/Properties.tsx';
import { useDialog } from '../../components/Dialog.tsx';
import { useToast } from '../../components/Toast.tsx';
import { formatDateTime, formatRelative, pluralise } from '../../lib/display.ts';
import { ApiError, authedFetch } from '../../lib/api.ts';
import { PdfPreview } from './PdfPreview.tsx';
import { startDrag } from '../drive/dragging.ts';
import { useQueryClient } from '@tanstack/react-query';
import { duplicateInPlace, payloadFor, useDriveClipboard } from '../drive/DriveClipboard.tsx';
import { refreshFolder } from '../drive/refreshFolder.ts';
import {
  useRenameSpreadsheet,
  useTrashSpreadsheet,
  type SpreadsheetSummary,
} from '../../lib/sheet-queries.ts';

/**
 * A spreadsheet in a Drive listing.
 *
 * Deliberately the same shape as a file row — the client's mental model is a
 * folder of documents, and a sheet being a different kind of thing underneath
 * is an implementation detail he should never have to think about.
 */
export function SheetRow({
  sheet,
  selected,
  onToggleSelected,
  selectionActive,
  view,
}: {
  sheet: SpreadsheetSummary;
  selected?: boolean;
  onToggleSelected?: (() => void) | undefined;
  /** True while a selection is being built — see ItemRow for why. */
  selectionActive?: boolean | undefined;
  /** A row in the table, or a tile in the grid. See ItemRow. */
  view?: 'list' | 'grid';
}) {
  const [renaming, setRenaming] = useState(false);
  /** Null when closed; the string is the blob URL, empty while it is made. */
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [draft, setDraft] = useState(sheet.name);

  const menu = useContextMenu();
  const dialog = useDialog();
  const toast = useToast();
  const navigate = useNavigate();

  const rename = useRenameSpreadsheet();
  const trash = useTrashSpreadsheet();
  const clipboard = useDriveClipboard();
  const client = useQueryClient();

  /** A copy beside the original, named "… (1)". */
  async function duplicate() {
    try {
      await duplicateInPlace(payloadFor('spreadsheet', sheet.id), sheet.folderId);
      await refreshFolder(client, sheet.folderId);
      toast.success(`"${sheet.name}" duplicated`);
    } catch (caught) {
      toast.error(
        'Could not duplicate that',
        caught instanceof ApiError ? caught.message : 'Please try again.',
      );
    }
  }

  async function commitRename() {
    const name = draft.trim();
    setRenaming(false);
    if (!name || name === sheet.name) {
      setDraft(sheet.name);
      return;
    }
    try {
      await rename.mutateAsync({ id: sheet.id, name });
      toast.success(`Renamed to "${name}"`);
    } catch (caught) {
      setDraft(sheet.name); // put the old name back rather than let the row lie
      toast.error(
        'Could not rename',
        caught instanceof ApiError ? caught.message : 'Please try again.',
      );
    }
  }

  async function remove() {
    const confirmed = await dialog.confirm({
      title: `Move "${sheet.name}" to the trash?`,
      message: 'Nothing is destroyed — trashed sheets can be restored.',
      details: [
        pluralise(sheet.sheetCount, 'tab'),
        `Last edited ${formatRelative(sheet.updatedAt)}`,
      ],
      confirmLabel: 'Move to trash',
      tone: 'danger',
    });
    if (!confirmed) return;

    try {
      await trash.mutateAsync(sheet.id);
      toast.success(`"${sheet.name}" moved to the trash`);
    } catch (caught) {
      toast.error(
        'Could not move to the trash',
        caught instanceof ApiError ? caught.message : 'Please try again.',
      );
    }
  }

  function showProperties() {
    dialog.open((close) => (
      <PropertiesPanel
        onClose={close}
        content={{
          name: sheet.name,
          kind: 'Spreadsheet',
          icon: <BrandIcon src={BRAND.excel} size={24} />,
          rows: [
            { label: 'Tabs', value: pluralise(sheet.sheetCount, 'tab'), numeric: true },
            { label: 'Created', value: formatDateTime(sheet.createdAt), numeric: true },
            {
              label: 'Modified',
              value: `${formatDateTime(sheet.updatedAt)} (${formatRelative(sheet.updatedAt)})`,
              numeric: true,
            },
            {
              // Not vanity: it is the number the editor uses to detect that
              // someone else saved first, so it is worth being able to see.
              label: 'Saved revisions',
              value: sheet.revision,
              numeric: true,
            },
            { label: 'Starred', value: sheet.isStarred ? 'Yes' : 'No' },
            { label: 'Identifier', value: sheet.id, numeric: true },
          ],
        }}
      />
    ));
  }

  /**
   * Convert to PDF and open the print dialog.
   *
   * Printing the sheet from the browser prints the application around it —
   * sidebar, toolbar and all. Going through the PDF means what reaches the
   * printer is the sheet as laid out, and the print dialog's own "Save as
   * PDF" is there for anyone who wanted the file rather than paper.
   */
  /** Fetch the PDF once and hand back a blob URL. */
  async function fetchPdf(): Promise<string> {
    const response = await authedFetch(`/api/sheets/${sheet.id}/export.pdf`, {
      credentials: 'include',
    });
    if (!response.ok) throw new Error('export failed');
    return URL.createObjectURL(await response.blob());
  }

  async function viewAsPdf() {
    setPreviewing(true);
    setPdfUrl(null);
    try {
      setPdfUrl(await fetchPdf());
    } catch {
      setPreviewing(false);
      toast.error(
        'Could not produce that PDF',
        'PDF conversion may be unavailable on this server.',
      );
    }
  }

  function closePreview() {
    // Revoked on close, or the whole document stays in memory for the life
    // of the tab.
    if (pdfUrl) URL.revokeObjectURL(pdfUrl);
    setPdfUrl(null);
    setPreviewing(false);
  }

  function downloadPreview() {
    if (!pdfUrl) return;
    const anchor = document.createElement('a');
    anchor.href = pdfUrl;
    anchor.download = `${sheet.name}.pdf`;
    anchor.click();
  }

  async function printAsPdf() {
    try {
      const response = await authedFetch(`/api/sheets/${sheet.id}/export.pdf`, {
        credentials: 'include',
      });
      if (!response.ok) throw new Error('export failed');

      const url = URL.createObjectURL(await response.blob());
      const frame = document.createElement('iframe');
      frame.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0';
      frame.src = url;
      frame.onload = () => {
        frame.contentWindow?.focus();
        frame.contentWindow?.print();
      };
      document.body.appendChild(frame);
    } catch {
      toast.error(
        'Could not prepare that for printing',
        'PDF conversion may be unavailable on this server.',
      );
    }
  }

  /**
   * The link dialog, raised through the dialog service like every other one
   * here — so it lands in the same place, closes on Escape, and holds the page
   * still behind it without this component knowing how any of that works.
   */
  function showLinkDialog() {
    dialog.open((close) => (
      <ShareLinkDialog kind="SPREADSHEET" targetId={sheet.id} name={sheet.name} onClose={close} />
    ));
  }

  const starItem = useStarMenuItem('spreadsheet', sheet.id, sheet.name, sheet.isStarred);

  const menuItems: MenuItem[] = [
    starItem,
    {
      label: 'Share a link…',
      icon: <Icons.Copy />,
      onSelect: () => showLinkDialog(),
    },

    { label: 'Open', icon: <SheetIcon />, onSelect: () => navigate(`/sheets/${sheet.id}`) },
    {
      label: 'View as PDF',
      icon: <Icons.Eye />,
      onSelect: () => void viewAsPdf(),
    },
    {
      label: 'Print as PDF',
      icon: <PrinterGlyph />,
      shortcut: 'Ctrl+P',
      onSelect: () => void printAsPdf(),
    },
    {
      label: 'Rename',
      icon: <Icons.Pencil />,
      shortcut: 'F2',
      onSelect: () => {
        setDraft(sheet.name);
        setRenaming(true);
      },
    },
    {
      label: 'Duplicate',
      icon: <Icons.Copy />,
      shortcut: 'Ctrl+D',
      onSelect: () => void duplicate(),
      separatorBefore: true,
    },
    {
      label: 'Copy',
      icon: <Icons.Copy />,
      shortcut: 'Ctrl+C',
      onSelect: () => clipboard.copy(payloadFor('spreadsheet', sheet.id)),
    },
    {
      label: 'Cut',
      icon: <Icons.Scissors />,
      shortcut: 'Ctrl+X',
      onSelect: () => clipboard.cut(payloadFor('spreadsheet', sheet.id)),
    },
    {
      label: 'Properties',
      icon: <InfoGlyph />,
      shortcut: 'Alt+Enter',
      onSelect: showProperties,
      separatorBefore: true,
    },
    {
      label: 'Move to trash',
      icon: <Icons.Trash />,
      shortcut: 'Del',
      tone: 'danger',
      onSelect: () => void remove(),
      separatorBefore: true,
    },
  ];

  /*
   * A tile in the grid.
   *
   * Everything above this — the menu, the rename, the dialogs — is the same
   * whichever way the Drive is being shown; only the shape differs. A sheet
   * and a letter live in the database rather than as a file, so there is no
   * picture to show and the tile shows what it is instead.
   */
  if (view === 'grid') {
    return (
      <>
        <li
          onContextMenu={menu.openAtPointer}
          onClick={(event) => {
            if (renaming) return;
            if ((event.target as HTMLElement).closest('button, input, a, label')) return;
            if (selectionActive) {
              onToggleSelected?.();
              return;
            }
            navigate(`/sheets/${sheet.id}`);
          }}
          className={cx(
            'group relative flex cursor-pointer flex-col gap-2 rounded-xl border p-2.5 transition-fast',
            selected
              ? 'border-[var(--blue-600)] bg-blue-50'
              : 'border-border bg-surface hover:bg-blue-50',
          )}
        >
          <div className="flex min-w-0 items-center gap-2">
            {onToggleSelected ? (
              <span
                className={cx(
                  'shrink-0 transition-fast',
                  selected || selectionActive ? 'opacity-100' : 'opacity-0 group-hover:opacity-100',
                )}
              >
                <SelectBox
                  checked={Boolean(selected)}
                  label={`Select ${sheet.name}`}
                  onChange={onToggleSelected}
                />
              </span>
            ) : null}

            <span className="shrink-0" aria-hidden>
              <BrandIcon src={BRAND.excel} size={20} />
            </span>

            <span
              className="min-w-0 flex-1 truncate text-body-sm font-medium text-ink"
              title={sheet.name}
            >
              {sheet.name}
            </span>

            <StarButton
              kind="spreadsheet"
              id={sheet.id}
              name={sheet.name}
              starred={sheet.isStarred}
            />
            <IconButton label={`Actions for ${sheet.name}`} onClick={menu.openUnder}>
              <KebabGlyph />
            </IconButton>
          </div>

          <div className="flex h-32 items-center justify-center overflow-hidden rounded-lg bg-sunken">
            <BrandIcon src={BRAND.excel} size={44} />
          </div>

          <p className="truncate text-micro text-muted">
            {pluralise(sheet.sheetCount, 'tab')} · {formatRelative(sheet.updatedAt)}
          </p>
        </li>

        {menu.position ? (
          <ContextMenu items={menuItems} position={menu.position} onClose={menu.close} />
        ) : null}
      </>
    );
  }

  return (
    <>
      <tr
        className={cx(
          'group h-row cursor-pointer border-b border-border transition-fast last:border-0 hover:bg-blue-50',
          selected && 'bg-blue-50',
        )}
        onContextMenu={menu.openAtPointer}
        // The whole row opens the sheet, not just its name.
        onClick={(event) => {
          if (renaming) return;
          if ((event.target as HTMLElement).closest('button, input, a, label')) return;

          // Extend the selection rather than navigating out of it.
          if (selectionActive) {
            onToggleSelected?.();
            return;
          }

          navigate(`/sheets/${sheet.id}`);
        }}
        draggable={!renaming}
        onDragStart={(event) =>
          startDrag(event, { kind: 'spreadsheet', id: sheet.id, name: sheet.name })
        }
      >
        <td className="pl-4">
          {onToggleSelected ? (
            <SelectBox
              checked={Boolean(selected)}
              label={`Select ${sheet.name}`}
              onChange={onToggleSelected}
            />
          ) : null}
        </td>

        <td className="px-4">
          <div className="flex min-w-0 items-center gap-2.5">
            <span className="shrink-0" aria-hidden>
              <BrandIcon src={BRAND.excel} size={20} />
            </span>

            {renaming ? (
              <input
                autoFocus
                value={draft}
                onChange={(event) => setDraft(event.target.value)}
                onBlur={() => void commitRename()}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') void commitRename();
                  if (event.key === 'Escape') {
                    setDraft(sheet.name);
                    setRenaming(false);
                  }
                }}
                aria-label="Sheet name"
                className="h-8 w-64 rounded-md border border-[var(--blue-600)] px-2 text-body-sm text-ink outline-none"
              />
            ) : (
              <Link
                to={`/sheets/${sheet.id}`}
                title={sheet.name}
                className="truncate text-body-sm font-medium text-ink hover:text-blue-700 hover:underline"
              >
                {sheet.name}
              </Link>
            )}
          </div>

          {/* The two columns the narrow layout drops, returned as a second
              line — the same fold the Drive's own rows do. Without it these
              cells were still drawn into a column the colgroup had squeezed to
              nothing, and the date printed straight over the name. */}
          <p className="mt-0.5 truncate pl-[30px] text-micro text-muted md:hidden">
            {pluralise(sheet.sheetCount, 'tab')} · {formatDateTime(sheet.updatedAt)}
          </p>
        </td>

        <td className="whitespace-nowrap px-4 text-right text-body-sm text-muted max-md:hidden">
          {pluralise(sheet.sheetCount, 'tab')}
        </td>

        <td
          className="whitespace-nowrap px-4 text-right text-body-sm text-muted max-md:hidden"
          data-numeric
        >
          {formatDateTime(sheet.updatedAt)}
        </td>

        <td className="px-4">
          <div className="flex items-center justify-end gap-0.5">
            <StarButton
              kind="spreadsheet"
              id={sheet.id}
              name={sheet.name}
              starred={sheet.isStarred}
            />
            <IconButton label={`Actions for ${sheet.name}`} onClick={menu.openUnder}>
              <KebabGlyph />
            </IconButton>
          </div>
        </td>
      </tr>

      {menu.position ? (
        <ContextMenu items={menuItems} position={menu.position} onClose={menu.close} />
      ) : null}

      {previewing ? (
        <PdfPreview
          title={sheet.name}
          url={pdfUrl}
          onClose={closePreview}
          onDownload={downloadPreview}
        />
      ) : null}
    </>
  );
}

/** A grid glyph, so a sheet is distinguishable from a document at a glance. */
function SheetIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 18 18"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.4"
    >
      <rect x="2.5" y="2.5" width="13" height="13" rx="1.5" />
      <path d="M2.5 6.5h13M2.5 10.5h13M7 6.5v9M11.5 6.5v9" />
    </svg>
  );
}

function KebabGlyph() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" aria-hidden>
      <circle cx="8" cy="3.5" r="1.4" />
      <circle cx="8" cy="8" r="1.4" />
      <circle cx="8" cy="12.5" r="1.4" />
    </svg>
  );
}

function InfoGlyph() {
  return (
    <svg
      width="15"
      height="15"
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.3"
    >
      <circle cx="8" cy="8" r="6.5" />
      <path d="M8 7.2v4" strokeWidth="1.8" strokeLinecap="round" />
      <circle cx="8" cy="4.9" r="0.7" fill="currentColor" stroke="none" />
    </svg>
  );
}

function PrinterGlyph() {
  return (
    <svg
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M6 9V3h12v6" />
      <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
      <rect x="6" y="14" width="12" height="7" rx="1" />
    </svg>
  );
}
